import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FunnelProgress } from '@/components/health-check/FunnelChrome';
import { BhcLogo } from '@/components/health-check/BhcHeader';
import { loadIntake, saveIntake } from '@/lib/bhc';
import '@/styles/business-health-check.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/health-check`;
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function downloadIcs(label, dateIso, slot) {
  const [time, ampm] = slot.split(' ');
  let [h, m] = time.split(':').map(Number);
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  const [Y, Mo, D] = dateIso.split('-');
  const start = `${Y}${Mo}${D}T${pad(h)}${pad(m)}00`;
  const endH = m === 30 ? (h + 1) : h;
  const endM = m === 30 ? '00' : '30';
  const end = `${Y}${Mo}${D}T${pad(endH)}${endM}00`;
  const ics = `BEGIN:VCALENDAR\r\nVERSION:2.0\r\nPRODID:-//KGOB//Health Check//EN\r\nBEGIN:VEVENT\r\nUID:${Date.now()}@kgob.com\r\nDTSTART;TZID=America/New_York:${start}\r\nDTEND;TZID=America/New_York:${end}\r\nSUMMARY:KGOB Health Check — Intake Call\r\nDESCRIPTION:Your Mid-Year Health Check intake call with KGOB. A secure video link will be emailed beforehand.\r\nLOCATION:Secure video call\r\nSTATUS:CONFIRMED\r\nEND:VEVENT\r\nEND:VCALENDAR\r\n`;
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'kgob-intake-call.ics'; a.click();
  URL.revokeObjectURL(url);
}

export default function Schedule() {
  const navigate = useNavigate();
  const intake = useMemo(() => loadIntake(), []);
  const isPaid = intake.status === 'paid' || !!intake.receipt;

  const [avail, setAvail] = useState(null);
  const [view, setView] = useState(() => new Date());
  const [selDate, setSelDate] = useState(null);
  const [selSlot, setSelSlot] = useState(null);
  const [booking, setBooking] = useState(intake.meeting || null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!intake.engagement_id || !isPaid) {
      navigate('/business-health-check', { replace: true });
      return;
    }
    fetch(`${API}/schedule/availability`).then((r) => r.json()).then((d) => {
      setAvail(d);
      const min = new Date(d.min_date + 'T00:00:00');
      setView(new Date(min.getFullYear(), min.getMonth(), 1));
    }).catch(() => setErr('Could not load availability. Please refresh.'));
  }, []); // eslint-disable-line

  const availSet = useMemo(() => new Set(avail?.available_dates || []), [avail]);
  const minMonth = avail ? new Date(avail.min_date + 'T00:00:00') : null;

  const confirm = async () => {
    if (!selDate || !selSlot || submitting) return;
    const label = `${DOW[selDate.getDay()]}, ${MONTHS[selDate.getMonth()]} ${selDate.getDate()}, ${selDate.getFullYear()} at ${selSlot} ET`;
    setSubmitting(true);
    setErr('');
    try {
      const res = await fetch(`${API}/schedule/book`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ engagement_id: intake.engagement_id, date: iso(selDate), slot: selSlot, label }),
      });
      if (!res.ok) throw new Error(`book ${res.status}`);
      const b = { date: iso(selDate), slot: selSlot, label };
      saveIntake({ ...loadIntake(), meeting: b, status: 'scheduled' });
      setBooking(b);
      window.scrollTo(0, 0);
    } catch {
      setErr('We couldn\u2019t confirm that time. Please pick another or call (704) 599-3355.');
      setSubmitting(false);
    }
  };

  const Header = () => (
    <header className="fheader"><div className="wrap fheader__bar">
      <BhcLogo />
      <span className="fheader__secure" style={{ marginLeft: 'auto' }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg> Booking secured</span>
    </div></header>
  );

  if (booking) {
    return (
      <div className="bhc-root">
        <Helmet><title>Intake call booked · KGOB</title>
        <meta name="description" content="Your KGOB Health Check intake call is booked." /><meta name="robots" content="noindex" /></Helmet>
        <Header />
        <FunnelProgress step={4} />
        <main className="funnel"><div className="wrap funnel__grid funnel__grid--narrow">
          <div className="success" data-testid="booking-confirmed">
            <div className="success__badge"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg></div>
            <span className="eyebrow eyebrow--center">Intake call booked</span>
            <h1 style={{ marginTop: '14px' }}>You're on the calendar.</h1>
            <div className="panel" style={{ textAlign: 'left', maxWidth: '460px', margin: '28px auto 0' }}>
              <p style={{ fontSize: '13px', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 600 }}>Confirmed</p>
              <p className="panel__title" style={{ marginTop: '8px' }} data-testid="confirmed-when">{booking.label}</p>
              <ul className="summary__incl" style={{ marginTop: '14px' }}>
                <li style={{ color: 'var(--ink)' }}><span className="tick">✓</span> About 30 minutes</li>
                <li style={{ color: 'var(--ink)' }}><span className="tick">✓</span> Secure video call</li>
                <li style={{ color: 'var(--ink)' }}><span className="tick">✓</span> With a member of our team</li>
              </ul>
            </div>
            <div className="actionbar" style={{ justifyContent: 'center', marginTop: '32px' }}>
              <button className="btn btn--primary btn--lg" type="button" onClick={() => downloadIcs(booking.label, booking.date, booking.slot)} data-testid="add-to-calendar">Add to calendar</button>
              <Link data-testid="schedule-link-1" className="btn btn--ghost btn--lg" to="/business-health-check/thank-you#checklist">Back to next steps</Link>
            </div>
            <p className="center muted" style={{ marginTop: '18px', fontSize: '13.5px' }}>A calendar invite and video link are on their way to your inbox.</p>
          </div>
        </div></main>
      </div>
    );
  }

  // calendar grid cells
  const cells = [];
  if (avail) {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const start = first.getDay();
    const daysIn = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    for (let i = 0; i < start; i++) cells.push(null);
    for (let dn = 1; dn <= daysIn; dn++) cells.push(new Date(view.getFullYear(), view.getMonth(), dn));
  }
  const prevDisabled = minMonth && view <= new Date(minMonth.getFullYear(), minMonth.getMonth(), 1);

  return (
    <div className="bhc-root">
      <Helmet><title>Schedule your intake call · KGOB</title><meta name="robots" content="noindex" /></Helmet>
      <Header />
      <FunnelProgress step={4} />

      <main className="funnel">
        <div className="wrap funnel__grid funnel__grid--narrow">
          <div className="funnel__head">
            <span className="eyebrow">Step 4 · Onboard</span>
            <h1>Schedule your intake call.</h1>
            <p>Pick a time that works — a member of our team will join you on a secure video call to confirm the records we need and get your engagement moving. Times are Eastern (ET).</p>
          </div>

          {err && <div className="callout callout--warn" style={{ marginBottom: '18px' }} data-testid="schedule-error">{err}</div>}

          <div className="booking" data-testid="booking-widget">
            <div className="booking__cal">
              <div className="cal__head">
                <b>{MONTHS[view.getMonth()]} {view.getFullYear()}</b>
                <div className="cal__nav">
                  <button type="button" disabled={prevDisabled} onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1))} aria-label="Previous month" data-testid="cal-prev">‹</button>
                  <button type="button" onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1))} aria-label="Next month" data-testid="cal-next">›</button>
                </div>
              </div>
              <div className="cal__grid">
                {DOW.map((d) => <div className="cal__dow" key={d}>{d}</div>)}
                {cells.map((d, i) => {
                  if (!d) return <div className="cal__day cal__day--empty" key={`e${i}`} />;
                  const avl = availSet.has(iso(d));
                  const sel = selDate && iso(d) === iso(selDate);
                  return (
                    <button key={iso(d)} type="button" disabled={!avl}
                      className={`cal__day${avl ? ' cal__day--avail' : ''}${sel ? ' cal__day--sel' : ''}`}
                      onClick={() => { setSelDate(d); setSelSlot(null); }}
                      data-testid={avl ? `cal-day-${iso(d)}` : undefined}>{d.getDate()}</button>
                  );
                })}
              </div>
            </div>
            <div className="booking__slots">
              <p className="slots__head" data-testid="slots-head">
                {selDate ? <>Times for <b>{DOW[selDate.getDay()]}, {MONTHS[selDate.getMonth()]} {selDate.getDate()}</b></> : 'Select a date to see available times.'}
              </p>
              {selDate && (
                <div className="slotlist">
                  {(avail?.slots || []).map((s) => (
                    <button key={s} type="button" className={`slot${selSlot === s ? ' slot--sel' : ''}`} onClick={() => setSelSlot(s)} data-testid={`slot-${s.replace(/[: ]/g, '')}`}>
                      <span>{s}</span><span className="slot__tz">ET</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="actionbar">
            <button type="button" className="btn btn--primary btn--lg" disabled={!selDate || !selSlot || submitting} onClick={confirm} data-testid="confirm-booking">
              {submitting ? 'Booking…' : (selSlot ? `Confirm ${selSlot} ET` : 'Select a time to confirm')} <span className="arrow">→</span>
            </button>
            <span className="actionbar__hint">No payment — your engagement is already confirmed.</span>
          </div>
        </div>
      </main>
    </div>
  );
}
